import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, Users, Save, Plus, Trash2, Upload, CreditCard, User, Mail, Phone, MapPin, Globe, Edit2, Search, CheckCircle, ScrollText, Palette, Image as ImageIcon, Receipt, ShieldCheck, AlertTriangle, KeyRound, Lock, MessageSquare, Zap } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import PaymentGatewaySettings from '@/components/settings/PaymentGatewaySettings';
import PincodeInput from '@/components/forms/PincodeInput';
import TaxCalendar from '@/pages/TaxCalendar';
import ApiKeysSettings from '@/components/settings/ApiKeysSettings';
import ChangePassword from '@/pages/ChangePassword';
import { useAuth } from '@/contexts/AuthContext';
import { INDIA_STATES, citiesForState } from '@/data/indiaStates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INDIAN_BANKS = [
  "State Bank of India",
  "HDFC Bank",
  "ICICI Bank",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Axis Bank",
  "Union Bank of India",
  "Bank of India",
  "Indian Bank",
  "Central Bank of India",
  "Indian Overseas Bank",
  "UCO Bank",
  "Yes Bank",
  "Kotak Mahindra Bank",
  "Other"
];

const StatusRow = ({ label, value, ok }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`inline-flex items-center gap-1.5 font-medium ${ok ? 'text-emerald-600' : 'text-slate-600'}`}>
      <span className={`h-2 w-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      {value}
    </span>
  </div>
);


const Settings = () => {
  const { user: me } = useAuth();
  const meRole = (me?.role || '').toLowerCase();
  const meIsAccounts = meRole === 'accounts';
  const meIsHr = meRole === 'hr';
  const meIsAgent = meRole === 'agent';
  // Role-scoped Settings tabs: Accounts → Compliance + Security; HR → Security;
  // Agent → Security only (no company/branding/payments/compliance/team/api-keys);
  // Admin → all.
  const meCanSeeTab = (tab) => {
    if (meIsAccounts) return tab === 'compliance' || tab === 'security';
    if (meIsHr) return tab === 'security';
    if (meIsAgent) return tab === 'security';
    return true;
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchingIfsc, setFetchingIfsc] = useState(false);
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [customBankName, setCustomBankName] = useState('');
  
  const [companySettings, setCompanySettings] = useState({
    companyName: '',
    gstNumber: '',
    tanNumber: '',
    panNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    website: '',
    logoUrl: '',
    bankName: '',
    bankBranch: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    accountType: 'current'
  });

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'agent',
    phone: '',
    isActive: true
  });

  useEffect(() => {
    fetchSettings();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setCompanySettings(response.data);
      if (response.data.bankName && !INDIAN_BANKS.includes(response.data.bankName)) {
        setCustomBankName(response.data.bankName);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.debug('Users list fetch failed (non-critical, non-admin):', error?.message || error);
    }
  };

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setCompanySettings(prev => ({ ...prev, [name]: value }));
  };

  const handleBankSelect = (value) => {
    if (value === 'Other') {
      setCompanySettings(prev => ({ ...prev, bankName: '' }));
    } else {
      setCompanySettings(prev => ({ ...prev, bankName: value }));
      setCustomBankName('');
    }
  };

  const handleCustomBankChange = (e) => {
    setCustomBankName(e.target.value);
    setCompanySettings(prev => ({ ...prev, bankName: e.target.value }));
  };

  const fetchIfscDetails = async () => {
    if (!companySettings.ifscCode || companySettings.ifscCode.length !== 11) {
      toast.error('Please enter a valid 11-character IFSC code');
      return;
    }

    setFetchingIfsc(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ifsc/${companySettings.ifscCode}`);
      if (response.data.valid) {
        setCompanySettings(prev => ({
          ...prev,
          bankName: response.data.bank,
          bankBranch: response.data.branch
        }));
        toast.success('Bank details fetched successfully!');
      } else {
        toast.error(response.data.error || 'Invalid IFSC code');
      }
    } catch (error) {
      toast.error('Failed to fetch IFSC details');
    } finally {
      setFetchingIfsc(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        ...companySettings,
        quoteExpiryDays:
          companySettings.quoteExpiryDays === '' || companySettings.quoteExpiryDays == null
            ? 7
            : Number(companySettings.quoteExpiryDays),
        fxMarkupPerUnitInr:
          companySettings.fxMarkupPerUnitInr === '' || companySettings.fxMarkupPerUnitInr == null
            ? 1
            : Number(companySettings.fxMarkupPerUnitInr),
      };
      await axios.put(`${BACKEND_URL}/api/settings`, payload);
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCompanySettings(prev => ({ ...prev, logoUrl: response.data.logoUrl }));
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const addUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error('Name and email are required');
      return;
    }
    if (!editingUser && !newUser.password) {
      toast.error('Password is required for a new user');
      return;
    }

    try {
      if (editingUser) {
        await axios.put(`${BACKEND_URL}/api/users/${editingUser.user_id}`, newUser);
        toast.success('User updated successfully!');
      } else {
        await axios.post(`${BACKEND_URL}/api/users`, newUser);
        toast.success('User added successfully!');
      }
      fetchUsers();
      setShowAddUser(false);
      setEditingUser(null);
      setNewUser({ name: '', email: '', password: '', role: 'agent', phone: '', isActive: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const editUser = (user) => {
    setNewUser({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      phone: user.phone || '',
      isActive: user.is_active
    });
    setEditingUser(user);
    setShowAddUser(true);
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/users/${userId}`);
      toast.success('User deleted successfully!');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700 border-purple-200',
      agent: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return colors[role] || colors.agent;
  };

  const isOtherBank = !INDIAN_BANKS.slice(0, -1).includes(companySettings.bankName) && companySettings.bankName !== '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-700 mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Loading settings...</p>
        </div>
      </div>
    );
  }

  const _qsTab = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab')) || 'company';
  const initialSettingsTab = meIsAccounts
    ? 'compliance'
    : (meIsHr || meIsAgent)
      ? 'security'
      : (['company', 'branding', 'payments', 'users'].includes(_qsTab) ? _qsTab : 'company');
  const _scopedTabs = meIsAccounts || meIsHr || meIsAgent;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 to-accent-600/5 rounded-3xl blur-3xl"></div>
        <div className="relative">
          <h1 className="text-2xl sm:text-3xl lg:text-5xl font-heading font-bold text-slate-900 tracking-tight mb-2 md:mb-3">
            Settings
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-slate-600 font-light">Manage your company details and team members</p>
        </div>
      </div>

      <Tabs defaultValue={initialSettingsTab} className="w-full">
        <TabsList className={`${_scopedTabs ? 'flex' : 'flex md:grid md:grid-cols-7'} w-full overflow-x-auto mb-6 md:mb-10 h-auto md:h-14 bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-1.5 shadow-sm`} style={{ scrollbarWidth: 'none' }}>
          {meCanSeeTab('company') && (
          <TabsTrigger value="company" data-testid="tab-company" className="rounded-xl shrink-0 whitespace-nowrap data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all">
            <Building2 className="h-4 w-4 mr-2" />Company Details
          </TabsTrigger>
          )}
          {meCanSeeTab('branding') && (
          <TabsTrigger value="branding" data-testid="tab-branding" className="rounded-xl shrink-0 whitespace-nowrap data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all">
            <Palette className="h-4 w-4 mr-2" />Branding
          </TabsTrigger>
          )}
          {meCanSeeTab('payments') && (
          <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-xl shrink-0 whitespace-nowrap data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all">
            <CreditCard className="h-4 w-4 mr-2" />Payment Gateway
          </TabsTrigger>
          )}
          {meCanSeeTab('compliance') && (
          <TabsTrigger value="compliance" data-testid="tab-compliance" className="rounded-xl shrink-0 whitespace-nowrap data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all">
            <ShieldCheck className="h-4 w-4 mr-2" />Compliance
          </TabsTrigger>
          )}
          {meCanSeeTab('users') && (
          <TabsTrigger value="users" data-testid="tab-users" className="rounded-xl shrink-0 whitespace-nowrap data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all">
            <Users className="h-4 w-4 mr-2" />Team Members
          </TabsTrigger>
          )}
          {meCanSeeTab('apikeys') && (
          <TabsTrigger value="apikeys" data-testid="tab-apikeys" className="rounded-xl shrink-0 whitespace-nowrap data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all">
            <KeyRound className="h-4 w-4 mr-2" />API Keys
          </TabsTrigger>
          )}
          {meCanSeeTab('security') && (
          <TabsTrigger value="security" data-testid="tab-security" className="rounded-xl shrink-0 whitespace-nowrap data-[state=active]:bg-gradient-to-br data-[state=active]:from-primary-700 data-[state=active]:to-primary-600 data-[state=active]:text-white data-[state=active]:shadow-lg transition-all">
            <Lock className="h-4 w-4 mr-2" />Security
          </TabsTrigger>
          )}
        </TabsList>

        {/* Company Details Tab */}
        <TabsContent value="company" className="animate-fade-in space-y-6">
          {/* TASK 1 — GST state-required prompt. Disappears once state is saved. */}
          {!companySettings.state ? (
            <div
              data-testid="gst-state-banner"
              className="flex items-start gap-3 rounded-2xl border-2 border-amber-200 bg-amber-50 px-5 py-4"
            >
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-amber-900">
                <span className="font-semibold">Agency state required</span> for GST supply-type auto-derivation.{' '}
                <button
                  type="button"
                  data-testid="gst-state-banner-link"
                  onClick={() => {
                    const el = document.getElementById('company-state-field');
                    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector('[role="combobox"]')?.focus(); }
                  }}
                  className="font-semibold underline underline-offset-2 hover:text-amber-700"
                >
                  Set it here.
                </button>
              </div>
            </div>
          ) : null}
          {/* Basic Info */}
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Building2 className="h-6 w-6 text-primary-700" />
              Company Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Company Name *</Label>
                <Input 
                  name="companyName" 
                  data-testid="input-company-name"
                  value={companySettings.companyName} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="Your Company Name"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">GST Number</Label>
                <Input 
                  name="gstNumber" 
                  data-testid="input-gst"
                  value={companySettings.gstNumber} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="22AAAAA0000A1Z5"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">TAN Number</Label>
                <Input 
                  name="tanNumber" 
                  data-testid="input-tan"
                  value={companySettings.tanNumber || ''} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="ABCD12345E"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">PAN Number</Label>
                <Input 
                  name="panNumber" 
                  data-testid="input-pan"
                  value={companySettings.panNumber || ''} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="ABCDE1234F"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Phone</Label>
                <Input 
                  name="phone" 
                  data-testid="input-company-phone"
                  value={companySettings.phone} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="+91 XXXXXXXXXX"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Email</Label>
                <Input 
                  name="email" 
                  data-testid="input-company-email"
                  type="email"
                  value={companySettings.email} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="contact@company.com"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Website</Label>
                <Input 
                  name="website" 
                  data-testid="input-website"
                  value={companySettings.website} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="https://www.yourcompany.com"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">IATA Number</Label>
                <Input
                  name="iataNumber"
                  data-testid="input-iata"
                  value={companySettings.iataNumber || ''}
                  onChange={handleSettingsChange}
                  className="h-12 rounded-xl"
                  placeholder="IATA accreditation no."
                />
                <p className="text-xs text-slate-500 mt-1">Printed on quote & invoice PDFs</p>
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">TAAI Number</Label>
                <Input
                  name="taaiNumber"
                  data-testid="input-taai"
                  value={companySettings.taaiNumber || ''}
                  onChange={handleSettingsChange}
                  className="h-12 rounded-xl"
                  placeholder="TAAI membership no."
                />
                <p className="text-xs text-slate-500 mt-1">Printed on quote & invoice PDFs</p>
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Default Quote Validity (days)</Label>
                <Input
                  name="quoteExpiryDays"
                  data-testid="input-quote-expiry-days"
                  type="number"
                  min="0"
                  value={companySettings.quoteExpiryDays ?? 7}
                  onChange={handleSettingsChange}
                  className="h-12 rounded-xl"
                  placeholder="7"
                />
                <p className="text-xs text-slate-500 mt-1">New quotes auto-expire after this many days</p>
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">FX Markup (₹ per unit)</Label>
                <Input
                  name="fxMarkupPerUnitInr"
                  data-testid="input-fx-markup"
                  type="number"
                  min="0"
                  step="0.01"
                  value={companySettings.fxMarkupPerUnitInr ?? 1}
                  onChange={handleSettingsChange}
                  className="h-12 rounded-xl"
                  placeholder="1.00"
                />
                <p className="text-xs text-slate-500 mt-1">Flat ₹ added to each foreign-currency unit — used by both INR-mode supplier costs and FX-mode quote collections</p>
              </div>

              <div className="md:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">PDF Footer Disclaimer</Label>
                <Textarea
                  name="pdfDisclaimer"
                  data-testid="input-pdf-disclaimer"
                  value={companySettings.pdfDisclaimer || ''}
                  onChange={handleSettingsChange}
                  className="rounded-xl min-h-[80px]"
                  placeholder="e.g. This is a computer-generated document. Terms apply."
                />
                <p className="text-xs text-slate-500 mt-1">Shown at the bottom of every generated PDF</p>
              </div>
            </div>
          </Card>

          {/* Address */}
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-3">
              <MapPin className="h-6 w-6 text-primary-700" />
              Address
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Address Line 1</Label>
                <Input 
                  name="addressLine1" 
                  data-testid="input-address1"
                  value={companySettings.addressLine1} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="Street address, building name"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Address Line 2</Label>
                <Input 
                  name="addressLine2" 
                  data-testid="input-address2"
                  value={companySettings.addressLine2} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="Area, landmark"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">City *</Label>
                <Input
                  name="city"
                  data-testid="input-city"
                  list="company-city-options"
                  value={companySettings.city}
                  onChange={handleSettingsChange}
                  className="h-12 rounded-xl"
                  placeholder={companySettings.state ? 'Select or type a city' : 'Select a state first'}
                />
                <datalist id="company-city-options">
                  {citiesForState(companySettings.state).map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>

              <div id="company-state-field">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">State *</Label>
                <Select
                  value={companySettings.state || ''}
                  onValueChange={(v) => setCompanySettings((s) => ({
                    ...s,
                    state: v,
                    // clear city if it no longer belongs to the newly selected state
                    city: citiesForState(v).includes(s.city) ? s.city : '',
                  }))}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="input-state"><SelectValue placeholder="Select state / UT" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {INDIA_STATES.map((st) => (
                      <SelectItem key={st} value={st} data-testid={`state-opt-${st}`}>{st}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div data-testid="settings-pincode-section">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Pincode</Label>
                <PincodeInput
                  value={companySettings.pincode || ''}
                  onChange={(v) => setCompanySettings((s) => ({ ...s, pincode: v }))}
                  onResolve={(r) => setCompanySettings((s) => ({
                    ...s,
                    city: r.city || s.city,
                    state: r.state || s.state,
                    country: r.country || s.country || 'India',
                  }))}
                  testId="settings-pincode-input"
                  placeholder="700001"
                />
              </div>
            </div>
          </Card>

          {/* Bank Details */}
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-3">
              <CreditCard className="h-6 w-6 text-primary-700" />
              Bank Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* IFSC with Auto-fetch */}
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">IFSC Code</Label>
                <div className="flex gap-2">
                  <Input 
                    name="ifscCode" 
                    data-testid="input-ifsc"
                    value={companySettings.ifscCode} 
                    onChange={handleSettingsChange} 
                    className="h-12 rounded-xl flex-1" 
                    placeholder="SBIN0001234"
                    maxLength={11}
                  />
                  <Button 
                    type="button" 
                    onClick={fetchIfscDetails}
                    disabled={fetchingIfsc}
                    data-testid="fetch-ifsc-btn"
                    className="h-12 px-4 rounded-xl bg-primary-700 text-white"
                  >
                    {fetchingIfsc ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Enter IFSC and click search to auto-fill bank details</p>
              </div>

              {/* Bank Name Dropdown */}
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Bank Name</Label>
                <Select 
                  value={isOtherBank ? 'Other' : companySettings.bankName} 
                  onValueChange={handleBankSelect}
                >
                  <SelectTrigger data-testid="select-bank" className="h-12 rounded-xl">
                    <SelectValue placeholder="Select bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDIAN_BANKS.map(bank => (
                      <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Bank Name (if Other selected) */}
              {(isOtherBank || companySettings.bankName === '' && customBankName) && (
                <div className="animate-fade-in">
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Custom Bank Name</Label>
                  <Input 
                    value={customBankName || companySettings.bankName} 
                    onChange={handleCustomBankChange}
                    data-testid="input-custom-bank"
                    className="h-12 rounded-xl" 
                    placeholder="Enter bank name"
                  />
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Branch</Label>
                <Input 
                  name="bankBranch" 
                  data-testid="input-bank-branch"
                  value={companySettings.bankBranch || ''} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="Branch name"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Account Holder Name</Label>
                <Input 
                  name="accountHolderName" 
                  data-testid="input-account-holder"
                  value={companySettings.accountHolderName} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="Account holder name"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Account Number</Label>
                <Input 
                  name="accountNumber" 
                  data-testid="input-account-number"
                  value={companySettings.accountNumber} 
                  onChange={handleSettingsChange} 
                  className="h-12 rounded-xl" 
                  placeholder="XXXXXXXXXXXX"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Account Type</Label>
                <Select 
                  value={companySettings.accountType || 'current'} 
                  onValueChange={(v) => setCompanySettings(prev => ({ ...prev, accountType: v }))}
                >
                  <SelectTrigger data-testid="select-account-type" className="h-12 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">Savings</SelectItem>
                    <SelectItem value="current">Current</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* Trip Themes */}
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-3">
              <MapPin className="h-6 w-6 text-primary-700" />
              Custom Trip Themes
            </h2>
            <p className="text-sm text-slate-500 mb-4">Add custom themes that will appear in the Trip Theme dropdown (in addition to default themes like Honeymoon, Family, etc.)</p>
            
            <div className="space-y-4">
              {/* Existing Custom Themes */}
              <div className="flex flex-wrap gap-2">
                {(companySettings.customThemes || []).map((theme, idx) => (
                  <div key={`theme-${theme}`} className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                    <span className="text-sm text-slate-700">{theme}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = (companySettings.customThemes || []).filter((_, i) => i !== idx);
                        setCompanySettings(prev => ({ ...prev, customThemes: updated }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Add New Theme */}
              <div className="flex gap-3">
                <Input
                  placeholder="Enter new theme name..."
                  className="flex-1 h-11 rounded-xl"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const newTheme = e.target.value.trim();
                      const existing = companySettings.customThemes || [];
                      if (!existing.includes(newTheme)) {
                        setCompanySettings(prev => ({ ...prev, customThemes: [...existing, newTheme] }));
                      }
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={(e) => {
                    const input = e.target.closest('.flex').querySelector('input');
                    if (input.value.trim()) {
                      const newTheme = input.value.trim();
                      const existing = companySettings.customThemes || [];
                      if (!existing.includes(newTheme)) {
                        setCompanySettings(prev => ({ ...prev, customThemes: [...existing, newTheme] }));
                      }
                      input.value = '';
                    }
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />Add
                </Button>
              </div>
            </div>
          </Card>

          {/* Itinerary inclusions / exclusions / T&C moved to Templates → Destinations. */}
          <Card className="p-6 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl" data-testid="card-templates-moved-note">
            <h2 className="text-lg font-heading font-bold text-slate-900 mb-1 flex items-center gap-3">
              <ScrollText className="h-5 w-5 text-primary-700" />
              Itinerary Inclusions &amp; Terms
            </h2>
            <p className="text-sm text-slate-500">
              Default inclusions, exclusions and Terms &amp; Conditions now live under{' '}
              <a href="/templates" className="text-primary-700 font-semibold underline">Templates → Destinations</a>{' '}
              — edit the <strong>Global Default</strong> template, or create one per destination.
            </p>
          </Card>

          {/* Default Invoice T&C — seeds termsAndConditions on every new
              invoice (manual + trip-based). Per-invoice override available
              via the Edit dialog. Fully independent of the itinerary T&C. */}
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl" data-testid="card-default-invoice-tnc">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2 flex items-center gap-3">
              <Receipt className="h-6 w-6 text-primary-700" />
              Default Invoice Terms & Conditions
            </h2>
            <p className="text-sm text-slate-500 mb-6">Used on every new invoice (manual and trip-based) and is fully <strong>independent</strong> of the Itinerary T&amp;C. Editable per-invoice from the invoice Edit dialog. Leave blank for no invoice T&amp;C.</p>
            <textarea
              name="defaultInvoiceTermsAndConditions"
              data-testid="input-default-invoice-tnc"
              value={companySettings.defaultInvoiceTermsAndConditions || ''}
              onChange={handleSettingsChange}
              rows={8}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
              placeholder={`1. Payment is due within 7 days of invoice date.\n2. GST as per applicable rates.\n3. All disputes subject to jurisdiction of <city>, India.\n4. Cancellation terms as per booking confirmation.`}
            />
          </Card>

          {/* Logo Upload */}
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Globe className="h-6 w-6 text-primary-700" />
              Company Logo
            </h2>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-32 h-32 bg-slate-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-300 overflow-hidden">
                {companySettings.logoUrl ? (
                  <img 
                    src={`${BACKEND_URL}${companySettings.logoUrl}`} 
                    alt="Company Logo" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Building2 className="h-12 w-12 text-slate-300" />
                )}
              </div>
              
              <div>
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <label htmlFor="logo-upload">
                  <Button type="button" variant="outline" asChild className="cursor-pointer rounded-xl">
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Logo
                    </span>
                  </Button>
                </label>
                <p className="text-sm text-slate-500 mt-2">Recommended: 200x200px, PNG or JPG</p>
              </div>
            </div>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={saveSettings} 
              disabled={saving}
              data-testid="save-settings-btn"
              className="bg-gradient-to-br from-primary-700 to-primary-600 text-white shadow-lg px-10 py-3 h-12 rounded-xl font-semibold"
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </TabsContent>

        {/* Branding Tab — About Us text + Destination Logos for itinerary
            page-2 plus Save button. Saves into the same /api/settings PUT
            payload. */}
        <TabsContent value="branding" className="animate-fade-in space-y-6" data-testid="branding-tab-content">
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2 flex items-center gap-3">
              <Building2 className="h-6 w-6 text-primary-700" />
              About Us
            </h2>
            <p className="text-sm text-slate-500 mb-6">Shown on Itinerary page 2 (left half) and any "About" sections in PDFs.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">About Tagline</Label>
                <Input
                  name="aboutTagline"
                  data-testid="input-about-tagline"
                  value={companySettings.aboutTagline || ''}
                  onChange={handleSettingsChange}
                  className="h-12 rounded-xl"
                  placeholder="Your gateway to unforgettable experiences"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">About Description</Label>
                <textarea
                  name="aboutText"
                  data-testid="input-about-text"
                  value={companySettings.aboutText || ''}
                  onChange={handleSettingsChange}
                  rows={6}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                  placeholder="Travcations is a premium travel agency specializing in handcrafted itineraries…"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Established (Year)</Label>
                <Input
                  name="established"
                  data-testid="input-established"
                  value={companySettings.established || ''}
                  onChange={handleSettingsChange}
                  className="h-12 rounded-xl"
                  placeholder="2014"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Team Size</Label>
                <Input
                  name="teamSize"
                  data-testid="input-team-size"
                  value={companySettings.teamSize || ''}
                  onChange={handleSettingsChange}
                  className="h-12 rounded-xl"
                  placeholder="25+ travel experts"
                />
              </div>
            </div>
          </Card>

          {/* Destination Logos — list-of-dicts editor. Each row has a name
              + logo URL. Used in the right half of Itinerary page 2. */}
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2 flex items-center gap-3">
              <ImageIcon className="h-6 w-6 text-primary-700" />
              Destination Logos
            </h2>
            <p className="text-sm text-slate-500 mb-6">Logos / icons rendered in the right-half grid on Itinerary page 2. Each row needs a name and image URL.</p>
            <div className="space-y-3" data-testid="destination-logos-list">
              {(companySettings.destinationLogos || []).map((entry, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-3 items-center p-3 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="col-span-1 flex items-center justify-center">
                    {entry.logoUrl ? (
                      <img src={entry.logoUrl.startsWith('http') ? entry.logoUrl : `${BACKEND_URL}${entry.logoUrl}`} alt={entry.name} className="h-10 w-10 object-contain rounded" />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-slate-300" />
                    )}
                  </div>
                  <Input
                    className="col-span-4 h-11 rounded-xl"
                    placeholder="Destination name (e.g. Bali)"
                    data-testid={`destination-logo-${idx}-name`}
                    value={entry.name || ''}
                    onChange={(e) => {
                      const next = (companySettings.destinationLogos || []).slice();
                      next[idx] = { ...next[idx], name: e.target.value };
                      setCompanySettings((p) => ({ ...p, destinationLogos: next }));
                    }}
                  />
                  <Input
                    className="col-span-6 h-11 rounded-xl font-mono text-xs"
                    placeholder="Logo URL (https://… or /api/uploads/…)"
                    data-testid={`destination-logo-${idx}-url`}
                    value={entry.logoUrl || ''}
                    onChange={(e) => {
                      const next = (companySettings.destinationLogos || []).slice();
                      next[idx] = { ...next[idx], logoUrl: e.target.value };
                      setCompanySettings((p) => ({ ...p, destinationLogos: next }));
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="col-span-1 text-red-500"
                    data-testid={`destination-logo-${idx}-remove`}
                    onClick={() => {
                      const next = (companySettings.destinationLogos || []).filter((_, i) => i !== idx);
                      setCompanySettings((p) => ({ ...p, destinationLogos: next }));
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                data-testid="add-destination-logo-btn"
                onClick={() => setCompanySettings((p) => ({
                  ...p,
                  destinationLogos: [...(p.destinationLogos || []), { name: '', logoUrl: '' }],
                }))}
              >
                <Plus className="h-4 w-4 mr-1" />Add Destination Logo
              </Button>
            </div>
          </Card>

          <div className="flex justify-end pt-2">
            <Button
              onClick={saveSettings}
              disabled={saving}
              data-testid="branding-save-btn"
              size="lg"
              className="bg-gradient-to-br from-primary-700 to-primary-600 hover:from-primary-800 hover:to-primary-700 text-white rounded-2xl px-8 h-14 shadow-xl"
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? 'Saving...' : 'Save Branding'}
            </Button>
          </div>
        </TabsContent>

        {/* Payment Gateway Tab */}
        <TabsContent value="payments" className="animate-fade-in">
          <PaymentGatewaySettings />
        </TabsContent>

        {/* Compliance — GST/TCS Tax Calendar */}
        <TabsContent value="compliance" className="animate-fade-in" data-testid="settings-compliance-tab">
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2 flex items-center gap-3">
              <ShieldCheck className="h-6 w-6 text-primary-700" /> Tax Calendar
            </h2>
            <p className="text-sm text-slate-500 mb-6">Track GST &amp; TCS filing/payment deadlines. Reminders surface in the notification bell 2 days before (and when overdue) until you mark them filed/paid.</p>
            <TaxCalendar embedded />
          </Card>
        </TabsContent>

        {/* Team Members Tab */}
        <TabsContent value="users" className="animate-fade-in">
          <Card className="p-6 sm:p-10 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-xl">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-900">Team Members</h2>
                <p className="text-slate-500 text-sm mt-1">Manage users who can access this CRM</p>
              </div>
              <Button 
                onClick={() => { setShowAddUser(true); setEditingUser(null); setNewUser({ name: '', email: '', password: '', role: 'agent', phone: '', isActive: true }); }}
                data-testid="add-user-btn"
                className="bg-gradient-to-br from-primary-700 to-primary-600 text-white rounded-xl px-6"
              >
                <Plus className="h-4 w-4 mr-2" />Add User
              </Button>
            </div>

            {/* Add/Edit User Form */}
            {showAddUser && (
              <div className="mb-8 p-6 bg-slate-50 rounded-2xl border border-slate-200 animate-fade-in">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">{editingUser ? 'Edit User' : 'Add New User'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Full Name *</Label>
                    <Input 
                      name="name" 
                      data-testid="input-user-name"
                      value={newUser.name} 
                      onChange={handleNewUserChange} 
                      className="h-11 rounded-xl" 
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Email *</Label>
                    <Input 
                      name="email" 
                      data-testid="input-user-email"
                      type="email"
                      value={newUser.email} 
                      onChange={handleNewUserChange} 
                      className="h-11 rounded-xl" 
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Phone</Label>
                    <Input 
                      name="phone" 
                      data-testid="input-user-phone"
                      value={newUser.phone} 
                      onChange={handleNewUserChange} 
                      className="h-11 rounded-xl" 
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Role *</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                      <SelectTrigger data-testid="select-user-role" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="accounts">Accounts</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                      {editingUser ? 'New Password' : 'Password *'}
                    </Label>
                    <Input 
                      name="password" 
                      type="password"
                      data-testid="input-user-password"
                      value={newUser.password} 
                      onChange={handleNewUserChange} 
                      className="h-11 rounded-xl" 
                      placeholder={editingUser ? 'Leave blank to keep current' : 'Set a password'}
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button onClick={addUser} data-testid="save-user-btn" className="bg-primary-700 text-white rounded-xl">
                    <Save className="h-4 w-4 mr-2" />{editingUser ? 'Update User' : 'Add User'}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowAddUser(false); setEditingUser(null); }} className="rounded-xl">
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Users List */}
            {users.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg">No team members added yet</p>
                <p className="text-slate-400 text-sm mt-1">Click "Add User" to add team members</p>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((user) => (
                  <div 
                    key={user.user_id} 
                    data-testid={`user-card-${user.user_id}`}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-4 mb-3 sm:mb-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center">
                        <User className="h-6 w-6 text-primary-700" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{user.name}</p>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                            {user.role}
                          </span>
                          {user.is_active === false && (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-100">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{user.email}</span>
                          {user.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{user.phone}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => editUser(user)}
                        data-testid={`edit-user-${user.user_id}`}
                        className="text-slate-600 hover:text-primary-700"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => deleteUser(user.user_id)}
                        data-testid={`delete-user-${user.user_id}`}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="apikeys" className="animate-fade-in" data-testid="settings-apikeys-tab">
          <ApiKeysSettings />
        </TabsContent>

        <TabsContent value="security" className="animate-fade-in" data-testid="settings-security-tab">
          <div className="max-w-2xl">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary-600" /> Account Security
              </h2>
              <p className="text-sm text-slate-500 mt-1">Change the password for your account. Choose a strong, unique password you don't use elsewhere.</p>
            </div>
            <ChangePassword embedded />
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default Settings;
